// Menu lines for a manual delivery: what an item and its picks cost, and the
// OrderInput the server prices. A port of the POS (ModifierModal.js and
// utilities/cartPayload.js): a manual delivery is charged like an in-store
// order, at BASE prices with no menu markup, and the server re-prices it.

const num = v => Number(v) || 0

const variantPricesOf = item => {
  const vp = item && item.variantPrices
  if (!vp) return {}
  return typeof vp === 'string' ? JSON.parse(vp) : vp
}

/** An item's sizes, cheapest first (Small, Medium, Large). */
export function variantKeysOf(item) {
  const vp = variantPricesOf(item)
  return Object.keys(vp).sort((a, b) => num(vp[a]) - num(vp[b]))
}

/** The item's own price at a size (or its base price). */
export function itemPrice(item, key) {
  const vp = variantPricesOf(item)
  if (key && vp[key] != null) return num(vp[key])
  return num(item && item.basePrice)
}

/** One unit of a modifier, at the item's size: FREE, by size, or fixed. */
export function modifierPrice(m, key) {
  if (!m || m.priceOverrideMode === 'FREE') return 0
  if (m.priceOverrideMode === 'VARIANT_BASED' && m.variantPrices) {
    const vp = typeof m.variantPrices === 'string' ? JSON.parse(m.variantPrices) : m.variantPrices
    if (key && vp[key] != null) return num(vp[key])
    const k = key && Object.keys(vp).find(x => x.toLowerCase() === String(key).toLowerCase())
    if (k) return num(vp[k])
    const vals = Object.values(vp).map(Number).filter(n => !Number.isNaN(n))
    return vals.length ? Math.min(...vals) : 0
  }
  return num(m.fixedPrice)
}

/** `n` of a modifier, honouring "first one free". */
export function modifierTotal(group, m, n, key) {
  if (n <= 0) return 0
  const unit = modifierPrice(m, key)
  return group.duplicatePricingMode === 'FIRST_FREE_THEN_CHARGED' ? unit * Math.max(0, n - 1) : unit * n
}

export const modifierIdOf = m => (m.modifier && m.modifier._id) || (m.item && m.item._id)
export const modifierNameOf = m => (m.modifier && m.modifier.name) || (m.item && m.item.name) || ''

/** Several of one modifier allowed (a stepper), rather than pick-or-not. */
export const isStepperGroup = g => !!g.allowDuplicates && !(g.selectionType === 'SINGLE' || g.maxSelections === 1)

export const isCustomizable = item =>
  variantKeysOf(item).length > 0 || (item.modifierGroups || []).length > 0 || (item.optionGroups || []).length > 0

/** The lines as the server's OrderInput[] (same shape as placePosOrder). */
export function buildOrderInput(lines) {
  return lines.map(l => {
    const input = {
      food: String(l.itemId),
      quantity: l.quantity,
      menuId: l.menuId ? String(l.menuId) : null,
      unitPrice: l.unitPrice || 0,
      specialInstructions: l.specialInstructions || null,
    }
    if (l.variationKey) input.variation = `${l.itemId}_${l.variationKey}`
    if (l.modifierSelections && l.modifierSelections.length) {
      input.modifierSelections = l.modifierSelections.map(g => ({
        modifierGroupId: String(g.modifierGroupId),
        modifierGroupName: g.modifierGroupName || '',
        selectedModifiers: g.selectedModifiers.map(m => ({
          modifierId: String(m.modifierId),
          modifierName: m.modifierName || '',
          price: m.price || 0,
          quantity: m.quantity || 1,
          ...(m.memberType ? { memberType: m.memberType } : {}),
          ...(m.itemId ? { itemId: String(m.itemId) } : {}),
        })),
      }))
    }
    return input
  })
}

/** "Large · Extra cheese, Olives · no onions", for the line under an item. */
export function lineDetails(line) {
  const parts = []
  if (line.variationTitle) parts.push(line.variationTitle)
  const mods = (line.modifierSelections || []).flatMap(g =>
    g.selectedModifiers.map(m => (m.quantity > 1 ? `${m.quantity}× ${m.modifierName}` : m.modifierName)),
  )
  if (mods.length) parts.push(mods.join(', '))
  if (line.specialInstructions) parts.push(line.specialInstructions)
  return parts.join(' · ')
}
